// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import {
  PreviewInvoiceBody,
  PreviewInvoiceItem,
  SubscriptionStateItem,
  UpdateQuantityBody,
} from 'presentations/api/schema/subscription';
import { AdminTeamUserListItem } from 'presentations/api/schema/team_user';

import { adminUserListPath, apiPath } from 'presentations/routes/path';
import { patch, post } from 'infras/rest_client';
import { useRequest } from 'presentations/views/hooks/request';
import {
  DashboardPortalContext,
  setIsOpenSeatEditForm,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { formatToSeconds, getDateTime } from 'infras/datetime';
import { Role } from 'domains/models/team_user/role';

interface Props {
  viewer: ViewerItem;
  onInvited?: (newUsers: AdminTeamUserListItem[]) => void;
}

export enum Command {
  Add = 1,
  Remove = 2,
}

let updatingSeatCount = 0;

export function useDashboardSeatEditForm(props: Props) {
  const t = translateInComponent(translations);

  const { state, dispatch } = useContext(DashboardPortalContext);
  const router = useRouter();

  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [isInitLoading, setIsInitLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [inputingQuantity, setInputingQuantity] = useState<number>(0);
  const [inputingCommand, setInputingCommand] = useState<Command>(Command.Add);
  const [prorationTime, setProrationTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [
    previewInvoiceItem,
    setPreviewInvoiceItem,
  ] = useState<PreviewInvoiceItem | null>(null);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem = userProfileRequest.data;

  const teamUsersRequest = useRequest(
    props.viewer.team && props.viewer.role <= Role.Admin
      ? apiPath(`/v1/admin/teams/${props.viewer.team!.uid}/users`)
      : null,
  );
  const teamUsers: AdminTeamUserListItem[] = teamUsersRequest.data || [];

  const subscriptionStateRequest = useRequest(
    props.viewer.team && props.viewer.role <= Role.Admin
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`)
      : null,
  );
  const subscriptionState: SubscriptionStateItem =
    subscriptionStateRequest.data;

  const upcomingInvoiceRequest = useRequest(
    props.viewer.team && props.viewer.role <= Role.Admin
      ? apiPath(`/v1/admin/teams/${props.viewer.team.uid}/invoices/upcoming`)
      : null,
  );

  const subscriptionRequest = useRequest(
    props.viewer.team && props.viewer.role <= Role.Admin
      ? apiPath(`/v1/admin/teams/${props.viewer.team.uid}/subscription`)
      : null,
  );

  useEffect(() => {
    setIsInitLoading(true);
  }, []);

  useEffect(() => {
    if (!subscriptionStateRequest.isLoding) {
      setIsInitLoading(false);
    }
  }, [subscriptionStateRequest.isLoading]);

  const calcNewQuantity = (command: Command, quantity: number) => {
    if (!subscriptionState) {
      return -1;
    }
    return (
      subscriptionState.quantity +
      (command === Command.Add ? quantity : -quantity)
    );
  };

  const preview = async (value: number, command: Command) => {
    setPreviewInvoiceItem(null);
    setError(null);
    if (value === 0) {
      return;
    }
    if (subscriptionState !== null) {
      const newQuantity = calcNewQuantity(command, value);
      if (newQuantity < 0) {
        setError(t('error zero quantity'));
        return;
      }

      if (newQuantity < teamUsers.length) {
        setError(t('error small quantity'));
        return;
      }

      setIsPreviewLoading(true);
      const prorationTime = getDateTime()
        .plus({ minutes: 1 })
        .startOf('minute');

      const data: PreviewInvoiceBody = {
        quantity: newQuantity,
        prorationTime: Math.floor(formatToSeconds(prorationTime)),
      };

      updatingSeatCount++;
      setTimeout(async () => {
        updatingSeatCount--;
        if (updatingSeatCount === 0) {
          const result = await post(
            apiPath(
              `/v1/admin/teams/${props.viewer.team!.uid}/invoices/preview`,
            ),
            { data },
          );
          if (!result.isSuccess) {
            setIsPreviewLoading(false);
            dispatch(setErrorMessage(t('error api failed')));
            return;
          }
          const previewInvoice: PreviewInvoiceItem = result.data;
          setPreviewInvoiceItem(previewInvoice);
          setIsPreviewLoading(false);
          setProrationTime(data.prorationTime);
        }
      }, 500);
    }
  };

  const onInputChangeQuantity = async (e: any) => {
    let inputValue = e.currentTarget.value;
    if (isNaN(inputValue)) {
      const convertValue = inputValue.replace(/[０-９]/g, function (s: string) {
        return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
      });
      if (!isNaN(convertValue)) {
        inputValue = convertValue;
      } else {
        return;
      }
    }
    const value = Number(inputValue);
    if (value < 0) {
      return;
    }
    setInputingQuantity(value);
    preview(value, inputingCommand);
  };

  const onInputChangeCommand = async (e: SyntheticEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    const command = Number(value);
    setInputingCommand(command);
    preview(inputingQuantity, command);
  };

  const onConfirm = async (e: SyntheticEvent<HTMLButtonElement>) => {
    setIsShowConfirm(true);
  };

  const onSubmit = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (!inputingQuantity || !subscriptionState) return;
    setIsLoading(true);
    const body: UpdateQuantityBody = {
      quantity: calcNewQuantity(inputingCommand, inputingQuantity),
      prorationTime: prorationTime!,
    };
    const result = await patch(
      apiPath(
        `/v1/admin/teams/${
          props.viewer.team!.uid
        }/subscription/update_quantity`,
      ),
      {
        data: body,
      },
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      dispatch(setErrorMessage(t('error api failed')));
      return;
    }
    setIsLoading(false);
    setIsShowConfirm(false);
    subscriptionStateRequest.mutate({
      ...subscriptionState,
      quantity: body.quantity,
    });
    upcomingInvoiceRequest.revalidate();
    subscriptionRequest.revalidate();
    dispatch(setIsOpenSeatEditForm(false));
    clear();
    dispatch(setSuccessMessage(t('Updated')));
  };

  const clear = () => {
    setInputingQuantity(0);
    setInputingCommand(Command.Add);
    setPreviewInvoiceItem(null);
  };

  const onCancel = async (e: SyntheticEvent<HTMLButtonElement>) => {
    dispatch(setIsOpenSeatEditForm(false));
    setTimeout(() => {
      clear();
    }, 10);
  };

  const onClick = (e: SyntheticEvent<HTMLButtonElement>) => {
    onCancel(e);
    router.replace(adminUserListPath());
  };
  const onCloseDialog = (e: SyntheticEvent<HTMLButtonElement>) => {
    setIsShowConfirm(false);
  };

  return {
    state: {
      userProfile,
      quantity: subscriptionState ? subscriptionState.quantity : 0,
      isOpen: state.isOpenSeatEditForm,
      isShowConfirm,
      isLoading: isLoading || isInitLoading,
      isPreviewLoading,
      inputingCommand,
      inputingQuantity,
      previewInvoiceItem,
      teamUsers,
      prorationTime,
      error,
    },
    actions: {
      onInputChangeQuantity,
      onInputChangeCommand,
      onConfirm,
      onCloseDialog,
      onSubmit,
      onCancel,
      onClick,
      calcNewQuantity,
    },
  };
}
